import { BsCheck } from "@react-icons/all-files/bs/BsCheck";
import type { MouseEvent } from "react";
import { memo } from "react";
import styled from "styled-components";

import { useCheckDailyTodo } from "@core/hooks/mutations/todo";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character } from "@core/types/character";

interface Props {
  character: Character;
  category: "chaos" | "guardian";
}

/**
 * 일일 숙제 셀 (혼돈의 균열 / 가디언 토벌).
 * - 클릭 / 우클릭 모두 전체 체크(allCheck=true) — 카드 뷰 DailyContents와 동일한 동작
 *   (이미 전부 체크된 상태에서 다시 누르면 서버가 토글 처리)
 * - 캐릭터의 settings.showChaos / showGuardian 이 false 면 "-" 표시
 */
const DayCheckCell = ({ character, category }: Props) => {
  const show =
    category === "chaos"
      ? character.settings.showChaos
      : character.settings.showGuardian;

  const currentCount =
    category === "chaos" ? character.chaosCheck : character.guardianCheck;
  const totalCount = category === "chaos" ? 2 : 1;

  const checkDailyTodo = useCheckDailyTodo({
    onSuccess: (updatedCharacter) => {
      updateCharacterQueryData({ character: updatedCharacter });
    },
  });

  if (!show) {
    return <Empty>-</Empty>;
  }

  const isDone = currentCount === totalCount;

  // 카드 뷰(DailyContents)와 동작 일치 — 클릭/우클릭 동일하게 전체 체크(allCheck=true)
  const performCheck = () => {
    checkDailyTodo.mutate({
      characterId: character.characterId,
      category,
      allCheck: true,
    });
  };

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    performCheck();
  };

  return (
    <Wrapper
      type="button"
      onClick={performCheck}
      onContextMenu={handleContextMenu}
      $isDone={isDone}
      aria-label={`${category === "chaos" ? "혼돈의 균열" : "가디언 토벌"} 체크`}
      title={isDone ? "다시 누르면 되돌립니다" : "클릭: 전체 체크"}
    >
      <Indicator $isDone={isDone}>
        {isDone ? <BsCheck /> : currentCount > 0 ? currentCount : ""}
      </Indicator>
      <Progress>
        {currentCount}/{totalCount}
      </Progress>
    </Wrapper>
  );
};

export default memo(DayCheckCell);

const Wrapper = styled.button<{ $isDone: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  min-height: 44px;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: ${({ $isDone }) => ($isDone ? 0.4 : 1)};

  &:hover {
    background: ${({ theme }) => theme.app.bg.gray1};
  }
`;

const Indicator = styled.div<{ $isDone: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background: ${({ $isDone, theme }) =>
    $isDone ? theme.app.palette.blue[350] : "transparent"};
  color: ${({ $isDone, theme }) =>
    $isDone ? theme.app.palette.gray[0] : theme.app.text.dark2};

  svg {
    font-size: 16px;
  }
`;

const Progress = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.app.text.light2};
`;

const Empty = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 44px;
  color: ${({ theme }) => theme.app.text.light2};
  font-size: 13px;
`;
